/**
 * Notice: Some code was adapted from Material-UI's text field.
 *         Copyright (c) 2014 Call-Em-All (https://github.com/callemall/material-ui)
 */
import { FormControl, FormHelperText, InputBase, InputLabel, Chip as MUIChip } from '@mui/material';
import { FormHelperTextProps as FormHelperTextProperties } from '@mui/material/FormHelperText';
import { InputBaseProps } from '@mui/material/InputBase';
import { InputLabelProps as InputLabelProperties } from '@mui/material/InputLabel';
import { red } from '@mui/material/colors';
import { Theme } from '@mui/material/styles';
import clsx from 'clsx';
import React from 'react';
import { BlurEvent } from 'react-autosuggest';

import { WithStyles, withStyles } from '@/hocs/with-styles';
import * as Types from '@/types';

const styles = (theme: Theme) => {
  const light = theme.palette.mode === 'light';

  return {
    root: {
      width: '100%',
    },
    inputRoot: {
      display: 'flex',
      justifyContent: 'center',
    },
    input: {
      appearance: 'none',
      WebkitTapHighlightColor: 'rgba(0,0,0,0)',
    },
    chipContainer: {
      display: 'flex',
      cursor: 'text',
      alignItems: 'center',
      marginBottom: -2,
      minHeight: 40,
      width: '100%',
    },
    labeled: {},
    label: {
      top: 4,
    },
    labelShrink: {
      top: 0,
    },
    helperText: {
      marginBottom: -20,
    },
    inkbar: {
      '&:after': {
        backgroundColor: theme.palette.primary[light ? 'dark' : 'light'],
        left: 0,
        bottom: 0,
        // Doing the other way around crash on IE11 "''" https://github.com/cssinjs/jss/issues/242
        content: '""',
        height: 2,
        position: 'absolute',
        right: 0,
        transform: 'scaleX(0)',
        transition: theme.transitions.create('transform', {
          duration: theme.transitions.duration.shorter,
          easing: theme.transitions.easing.easeOut,
        }),
        pointerEvents: 'none',
      },
      '&$focused:after': {
        transform: 'scaleX(1)',
      },
    },
    focused: {},
    disabled: {},
    error: {
      '&:after': {
        backgroundColor: red.A400,
        transform: 'scaleX(1)',
      },
    },
    chip: {
      marginRight: theme.spacing(1),
    },
  };
};

interface Chip {
  key: string;
  value: string;
}

export interface ChipRendererArgs {
  value: string | Chip;
  text: React.ReactNode | string;
  chip: Chip;
  isDisabled: boolean;
  isFocused: boolean;
  handleClick: () => void;
  handleDelete: () => void;
  className: string;
}

export const defaultChipRenderer = (
  { value, text, isFocused, isDisabled, handleClick, handleDelete, className }: ChipRendererArgs,
  key: React.ReactText,
) => (
  <MUIChip
    key={key}
    className={className}
    style={{ pointerEvents: isDisabled ? 'none' : undefined, backgroundColor: isFocused ? 'blue' : undefined }}
    onClick={handleClick}
    onDelete={handleDelete}
    label={text}
  />
);

interface Properties {
  allowDuplicates: boolean;
  blurBehavior?: 'clear' | 'add' | 'ignore';
  chipRenderer: (args: ChipRendererArgs, key: React.ReactText) => React.ReactNode;
  className?: string;
  clearInputValueOnChange: boolean;
  dataSourceConfig?: {
    value: string;
    text: string;
  };

  disabled?: boolean;
  error?: boolean;
  FormHelperTextProps?: FormHelperTextProperties;
  fullWidth?: boolean;
  fullWidthInput?: boolean;
  helperText?: React.ReactNode;
  id?: React.ReactText;
  InputProps?: Partial<Omit<InputBaseProps, 'id'>>;
  inputValue?: string;
  inputRef?: (ref: unknown) => void;
  InputLabelProps?: Partial<Omit<InputLabelProperties, 'htmlFor'>>;
  label?: React.ReactNode;
  newChipKeyCodes: number[];
  onAdd?: (chip: Chip) => void;
  onBlur?: (event: React.FocusEvent<unknown>, params?: BlurEvent<Types.Suggestion>) => void;
  onDelete?: (chip: Chip, index: number) => void;
  onFocus?: (e: React.FocusEvent) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onEnter?: (e: React.KeyboardEvent) => void;
  onUpdateInput?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  rootRef?: (ref: unknown) => void;
  value?: Chip[];
}

interface State {
  isFocused: boolean;
  errorText?: string;
  isClean: boolean;
  chips: Chip[];
  focusedChip: number | null;
  inputValue?: string;
  keyPressed?: boolean;
  preventChipCreation?: boolean;
  clearInputValueOnChange: boolean;
  value?: Chip[];
}
class ChipInput extends React.Component<Properties & WithStyles<typeof styles>, State> {
  state: State = {
    isFocused: false,
    isClean: true,
    chips: [],
    focusedChip: null,
    inputValue: this.props?.inputValue,
    clearInputValueOnChange: this.props.clearInputValueOnChange,
    value: this.props?.value,
  };

  private inputBlurTimeout: NodeJS.Timer | undefined;
  private input: unknown;
  private actualInput: Window | undefined;

  componentWillUnmount() {
    if (this.inputBlurTimeout) {
      clearTimeout(this.inputBlurTimeout);
    }
  }

  static getDerivedStateFromProps(nextProps: Properties & WithStyles<typeof styles>, prevState: State): State {
    if (nextProps.disabled) {
      return {
        ...prevState,
        focusedChip: null,
      };
    }
    if (nextProps.value && prevState.clearInputValueOnChange && nextProps.value.length !== prevState.value?.length) {
      if (prevState.inputValue && nextProps.onUpdateInput) {
        nextProps.onUpdateInput({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
      } else {
        return { ...prevState, inputValue: '' };
      }
    }

    return prevState;
  }

  /**
   * Blurs this component.
   * @public
   */
  blur() {
    if (this.actualInput) {
      this.actualInput.blur();
    }
  }
  /**
   * Focuses this component.
   * @public
   */
  focus = () => {
    if (this.actualInput) {
      this.actualInput.focus();
    }

    // this.getInputNode().focus()
    // if (this.props.openOnFocus && !this.props.disabled) {
    // }
    if (this.state.focusedChip) {
      this.setState({ focusedChip: null });
    }
  };

  handleInputBlur = () => {
    if (this.props.onBlur) {
      this.props.onBlur(event as unknown as React.FocusEvent);
    }

    this.setState({ isFocused: false });

    if (this.state.focusedChip) {
      this.setState({ focusedChip: null });
    }

    if (this.props.inputValue) {
      const numChipsBefore = (this.props.value || this.state.chips).length;
      const value = this.props.inputValue;

      // this.inputBlurTimeout = setTimeout(() => {
      //   const numChipsAfter = (this.props.value || this.state.chips).length;
      //   if (numChipsBefore === numChipsAfter) {
      //     this.handleAddChip(value);
      //   } else {
      //     // this.clearInput();
      //   }
      // }, 150);
    }
  };

  handleInputFocus: InputBaseProps['onFocus'] = (event) => {
    this.setState({ isFocused: true });
    if (this.props.onFocus) {
      this.props.onFocus(event);
    }
  };

  handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { focusedChip } = this.state;

    this.setState({ keyPressed: false, preventChipCreation: false });
    if (this.props.onKeyDown) {
      // Needed for arrow controls on menu in autocomplete scenario
      this.props.onKeyDown(event);
      // Check if the callback marked the event as isDefaultPrevented() and skip further actions
      // enter key for example should not always add the current value of the inputField
      if (event.isDefaultPrevented()) {
        return;
      }
    }
    if (this.props.newChipKeyCodes.indexOf(event.keyCode) >= 0) {
      if (this.props.onEnter) {
        this.props.onEnter(event);
      } else {
        // const result = this.handleAddChip(event.target.value);
        // if (result !== false) {
        //   event.preventDefault();
        // }
      }
    } else if (event.keyCode === 8 || event.keyCode === 46) {
      if ((event.target as HTMLInputElement).value === '') {
        const chips = this.props.value || this.state.chips;
        if (focusedChip === null && event.keyCode === 8) {
          this.setState({ focusedChip: chips.length - 1 });
        } else if (focusedChip !== null) {
          const chipsInner = this.props.value || this.state.chips;
          const value = chipsInner[focusedChip];
          this.handleDeleteChip(value, focusedChip);
          if (event.keyCode === 8 && focusedChip > 0) {
            this.setState({ focusedChip: focusedChip - 1 });
          } else if (event.keyCode === 46 && focusedChip <= chipsInner.length - 1) {
            this.setState({ focusedChip });
          }
        }
      }
    } else if (event.keyCode === 37) {
      const chips = this.props.value || this.state.chips;
      if (focusedChip === null && (event.target as HTMLInputElement).value === '' && chips.length) {
        return this.setState({ focusedChip: chips.length - 1 });
      }
      if (focusedChip !== null && focusedChip > 0) {
        this.setState({ focusedChip: focusedChip - 1 });
      }
    } else if (event.keyCode === 39) {
      const chips = this.props.value || this.state.chips;
      if (focusedChip !== null && focusedChip < chips.length - 1) {
        this.setState({ focusedChip: focusedChip + 1 });
      } else {
        this.setState({ focusedChip: null });
      }
    } else {
      this.setState({ focusedChip: null });
    }
  };

  handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      !this.state.preventChipCreation &&
      this.props.newChipKeyCodes.indexOf(event.keyCode) > 0 &&
      this.state.keyPressed
    ) {
      this.clearInput();
    } else {
      if (!this.props.inputValue) {
        this.setState({ inputValue: (event.target as HTMLInputElement).value });
      }
    }
  };

  handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    this.setState({ keyPressed: true });
  };

  handleUpdateInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!this.props.inputValue) {
      this.setState({ inputValue: e.target.value });
    }
    if (this.props.onUpdateInput) {
      this.props.onUpdateInput(e);
    }
  };

  handleDeleteChip(chip: Chip | string, i: number) {
    if (this.props.value) {
      if (this.props.onDelete && typeof chip === 'object') {
        this.props.onDelete(chip, i);
      }
    } else {
      const chips = this.state.chips.slice();
      const changed = chips.splice(i, 1); // remove the chip at index i
      if (changed) {
        let { focusedChip } = this.state;

        if (focusedChip !== null) {
          switch (true) {
            case focusedChip === i:
              focusedChip = null;
              break;
            case focusedChip! > i:
              focusedChip!--;
              break;
            default:
              break;
          }
        }

        this.setState({ chips, focusedChip });
      }
    }
  }
  /**
   * Clears the text field for adding new chips.
   * @public
   */
  clearInput() {
    if (!this.props.inputValue) {
      this.setState({ inputValue: '' });
    }
    if (this.props.onUpdateInput) {
      this.props.onUpdateInput({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
    }
  }
  /**
   * Sets a reference to the Material-UI Input component.
   * @param {object} input - The Input reference
   */
  setInputRef = (input: unknown) => {
    this.input = input;
  };

  /**
   * Set the reference to the actual input, that is the input of the Input.
   * @param {object} ref - The reference
   */
  setActualInputRef = (ref: unknown) => {
    this.actualInput = ref as Window;
    if (this.props.inputRef) {
      this.props.inputRef(ref);
    }
  };

  render() {
    const {
      allowDuplicates,
      children,
      chipRenderer = defaultChipRenderer,
      classes,
      className,
      clearInputValueOnChange,
      dataSourceConfig,
      inputValue,
      disabled,
      error,
      FormHelperTextProps,
      fullWidth,
      fullWidthInput,
      helperText,
      id,
      InputProps,
      inputRef,
      InputLabelProps,
      label,
      newChipKeyCodes,
      onAdd,
      onBlur,
      onDelete,
      onFocus,
      onKeyDown,
      onEnter,
      onUpdateInput,
      placeholder,
      required,
      rootRef,
      value,
      ...other
    } = this.props;

    const chips = this.props.value || this.state.chips;
    const chipInputValue = onUpdateInput ? inputValue : this.state.inputValue;
    const hasInput = chips.length > 0 || (chipInputValue?.length ?? 0) > 0;
    const shrinkFloatingLabel = label !== null && (hasInput || this.state.isFocused);

    return (
      <FormControl
        variant="standard"
        // ref={rootRef}

        fullWidth={fullWidth}
        className={clsx(className, classes.root)}
        error={!!error}
        required={required}
        onClick={this.focus}
        disabled={disabled}
        {...other}
      >
        {label && (
          <InputLabel
            htmlFor={String(id)}
            classes={{ root: classes.label, shrink: classes.labelShrink }}
            shrink={shrinkFloatingLabel}
            focused={this.state.isFocused}
            {...InputLabelProps}
          >
            {label}
          </InputLabel>
        )}
        <div
          className={clsx(classes.chipContainer, {
            [classes.focused]: this.state.isFocused,
            [classes.disabled]: disabled,
            [classes.labeled]: label !== null,
            [classes.error]: !!error,
          })}
        >
          {chips.map((tag, i) => {
            const chipValue = dataSourceConfig ? tag[dataSourceConfig.value as keyof Chip] : tag;
            return chipRenderer(
              {
                value: chipValue,
                text: dataSourceConfig ? tag[dataSourceConfig.text as keyof Chip] : tag,
                chip: tag,
                isDisabled: !!disabled,
                isFocused: this.state.focusedChip === i,
                handleClick: () => {
                  this.setState({ focusedChip: i });
                  // if (this.state.focusedChip === i) {
                  //   this.handleEditChip(chipValue, i);
                  // } else {
                  //   this.setState({ focusedChip: i });
                  // }
                },
                handleDelete: () => this.handleDeleteChip(chipValue, i),
                className: classes.chip,
              },
              String(i),
            );
          })}
          <InputBase
            // ref={this.setInputRef}
            classes={{ input: classes.input, root: classes.inputRoot }}
            id={String(id)}
            value={chipInputValue}
            onChange={this.handleUpdateInput}
            onKeyDown={this.handleKeyDown}
            onKeyPress={this.handleKeyPress}
            onKeyUp={this.handleKeyUp}
            onFocus={this.handleInputFocus}
            onBlur={this.handleInputBlur}
            inputRef={this.setActualInputRef}
            disabled={disabled}
            fullWidth={fullWidthInput}
            placeholder={!hasInput && (shrinkFloatingLabel || !label) ? placeholder : undefined}
            {...other}
            {...InputProps}
          />
        </div>
        {helperText && (
          <FormHelperText
            {...FormHelperTextProps}
            className={
              FormHelperTextProps ? clsx(FormHelperTextProps.className, classes.helperText) : classes.helperText
            }
          >
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    );
  }
}
export default withStyles(styles)(ChipInput);
